
/*
 * Password complexity check modeled off of app/view/static/js/zr/zr.pw_complex_check.
 */

import apiRequest from '@zr/utilities/api';

import { THRESHOLD_GOOD_PASSWORD_LENGTH, THRESHOLD_VERY_GOOD_PASSWORD_LENGTH } from './constants';

const CHECK_PASSWORD_URL = '/auth/password_banned';

export const BANNED_RESPONSE = {
  message: 'This password is too common. Please use a different one.',
  type: 'error',
};

export const MODERATE_RESPONSE = {
  message: 'Password strength is medium. Longer would be better.',
  type: 'warning',
};

export const GOOD_RESPONSE = {
  message: 'Password strength is good. Longer would be better.',
  type: 'warning',
};

export const VERY_GOOD_RESPONSE = {
  message: 'Password strength is great!',
  type: 'success',
};

// cache pw requests to avoid rate limit issues with /auth/banned_password
// cache keys are the attempted passwords, values are like { banned: true | false }
let cache = {};
let cacheCount = 0;

const pwLengthFeedback = (len) => {
  if (len < THRESHOLD_GOOD_PASSWORD_LENGTH) { return MODERATE_RESPONSE; }
  if (len < THRESHOLD_VERY_GOOD_PASSWORD_LENGTH) { return GOOD_RESPONSE; }
  return VERY_GOOD_RESPONSE;
};

export const resetPwCache = () => {
  cache = {};
  cacheCount = 0;
};

/**
 * @typedef {Promise} pwFeedback resolves to an object with the following properties
 * @property {string} message message for the user providing feedback on the quality of the password
 * @property {string} type description of the type of message, one of: error, warning, success
*/

/**
 * Password complexity check
 * @param {string} password the attempted password value
 * @return {pwFeedback}
 */

export const checkPwComplexity = async (password) => {
  // pw is in the cache
  if (Object.keys(cache).includes(password)) {
    if (cache[password].banned) {
      return BANNED_RESPONSE;
    }
    return pwLengthFeedback(password.length);
  }

  // pw not in cache, make request
  const params = new URLSearchParams({ password });

  const fetchInit = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body: params.toString(),
  };

  const response = await apiRequest(CHECK_PASSWORD_URL, fetchInit);
  const json = await response.json();

  cache[password] = { banned: !!json.banned };
  cacheCount += 1;

  if (cacheCount > 100) {
    resetPwCache();
  }

  if (json.banned) {
    return BANNED_RESPONSE;
  }

  return pwLengthFeedback(password.length);
};
